import { ComponentPropsWithRef, ReactNode } from 'react';

interface IconButtonProps extends ComponentPropsWithRef<'button'> {
    variant?: 'default' | 'danger' | 'danger-muted' | 'success' | 'accent' | 'primary' | 'surface';
    size?: 'sm' | 'md' | 'lg';
    children: ReactNode;
}

/**
 * Circular icon button with consistent styling across the app.
 * Variants:
 * - default: Dark gray circular button (for edit, close, menu actions)
 * - primary: Blue circular button (for save/submit actions)
 * - success: Teal/cyan circular button (for confirm actions)
 * - danger: Red circular button (for delete actions)
 * - accent: Accent-colored button
 * - surface: Translucent bordered circle for controls that float over content
 */
export function IconButton({
    variant = 'default',
    size = 'md',
    className = '',
    children,
    ...props
}: IconButtonProps) {
    const baseClasses =
        'inline-flex items-center justify-center rounded-full transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = {
        default:
            'bg-gray-600 text-gray-300 hover:bg-gray-500 hover:text-white focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white',
        primary:
            'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500 dark:bg-blue-600 dark:hover:bg-blue-500',
        danger:
            'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500 dark:bg-red-600 dark:hover:bg-red-500',
        'danger-muted':
            'bg-red-900/70 text-white hover:bg-red-800 focus:ring-red-900 dark:bg-red-900/80 dark:hover:bg-red-800',
        success:
            'bg-teal-500 text-white hover:bg-teal-600 focus:ring-teal-500 dark:bg-teal-500 dark:hover:bg-teal-400',
        accent:
            'bg-accent text-white hover:bg-accent-hover focus:ring-accent dark:bg-primary-light dark:hover:bg-teal-400',
        surface:
            'bg-white/90 text-gray-600 border border-gray-300 shadow-xs backdrop-blur-xs hover:bg-white hover:text-gray-900 focus:ring-accent-hover focus:ring-offset-gray-50 dark:focus:ring-primary-light dark:focus:ring-offset-dark dark:bg-secondary/90 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-secondary dark:hover:text-white',
    };

    const sizeClasses = {
        sm: 'p-2 min-w-[32px] min-h-[32px]',
        md: 'p-2.5 min-w-[40px] min-h-[40px]',
        lg: 'p-3 min-w-[48px] min-h-[48px]',
    };

    return (
        <button
            className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
            {...props}
        >
            {children}
        </button>
    );
}
